import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { OpenFeature } from '@openfeature/web-sdk';

export interface FeatureFlag {
  readonly key: string;
  readonly defaultValue: boolean;
}

// Catalog of feature flags
export const FEATURE_FLAGS = {
  sampleInferenceFlexibilityUi: {
    key: 'sample-inference-flexibility-ui',
    defaultValue: false,
  },
  sampleInferenceAdditionalUpload: {
    key: 'sample-inference-additional-upload',
    defaultValue: false,
  },
  reportsTab: {
    key: 'reports-tab',
    defaultValue: false,
  },
} as const satisfies Record<string, FeatureFlag>;

/**
 * Read a flag from inside a React component. This is a hook (re-renders when
 * the flag value changes), so it can only be called during render. For
 * non-render code (e.g. a route `beforeLoad`) use
 * `getFeatureFlagOutsideReact` instead.
 */
export const useFeatureFlag = (flag: FeatureFlag): boolean =>
  useBooleanFlagValue(flag.key, flag.defaultValue);

/**
 * Read a flag from outside React render (e.g. a route `beforeLoad`, where hooks
 * can't run). Reads synchronously from the global OpenFeature provider; if the
 * provider isn't ready yet it returns the flag's default. In components, prefer
 * the `useFeatureFlag` hook so the UI reacts to flag changes.
 */
export const getFeatureFlagOutsideReact = (flag: FeatureFlag): boolean =>
  OpenFeature.getClient().getBooleanValue(flag.key, flag.defaultValue);

// The flag-configuration shape `TypedInMemoryProvider` expects for a boolean
// flag: `defaultVariant` must be a key of `variants` ('on' | 'off'), so it is
// typed as a literal union rather than a widened string.
export type BooleanFlagConfiguration = Record<
  string,
  {
    disabled: boolean;
    variants: { on: true; off: false };
    defaultVariant: 'on' | 'off';
  }
>;

/**
 * Build the in-memory provider configuration for a set of boolean flags. Used
 * by the test fixture to shape an in-memory OpenFeature provider.
 */
export const buildBooleanFlagConfiguration = (
  flags: Record<string, boolean>
): BooleanFlagConfiguration => {
  const configuration: BooleanFlagConfiguration = {};
  for (const [flagKey, enabled] of Object.entries(flags)) {
    configuration[flagKey] = {
      disabled: false,
      variants: { on: true, off: false },
      defaultVariant: enabled ? 'on' : 'off',
    };
  }
  return configuration;
};
